/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

// import dayjs from 'dayjs';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// import isBetween from 'dayjs/plugin/isBetween'; // on en a besoin

import { AssistantContext } from '@/contexts';
import type {
  AssistantContextType,
  ResetOptions,
} from '@/contexts/AssistantContext';
import { DEFAULT_PACS } from '@/constants';
import {
  computeNextPacsNumber,
  formatPrenomSaisie,
  fusionnerPrefillHubee,
  mapTeledossierToPacs,
} from '@/utils';
import { useParametresStore, useRegistreStore } from '@/zustand';

type AssistantContextProviderProps = {
  children: React.ReactNode;
};

const AssistantContextProvider = ({
  children,
}: AssistantContextProviderProps) => {
  type PacsChangeValue = string | number | boolean | null;

  const [pacs, setPacs] = React.useState<any>(DEFAULT_PACS);
  const [errors, setErrors] = React.useState<any>({});
  const [teledossierData, setTeledossierData] = React.useState<any>(null);
  const [isLoadingHubeeData, setIsLoadingHubeeData] =
    React.useState<boolean>(false);
  const [isDissolutionMode, setIsDissolutionMode] =
    React.useState<boolean>(false);
  const [selectedDeclarationType, setSelectedDeclarationType] = React.useState<
    string | null
  >(null);
  const [selectedPacs, setSelectedPacs] = React.useState<number | null>(null);
  const [computedPacsNumber, setComputedPacsNumber] = React.useState<
    string | null
  >(null);

  const [resetVersion, setResetVersion] = React.useState<number>(0);

  const { initLieuNaissance, initDates, initLieuDomicile } = useParametresStore(
    (state) => state.parametresUser,
  );

  const registre = useRegistreStore((state) => state.registre);
  const signataire = useParametresStore((state) => state.defaultSignataire);

  // Champs issus de la demande Hubee en cours de traitement (null hors mode
  // Hubee). Mémoïsé pour ne pas relancer l'initialisation à chaque rendu.
  const hubeePrefill = React.useMemo(
    () => (teledossierData ? mapTeledossierToPacs(teledossierData) : null),
    [teledossierData],
  );

  // Numéro déjà présent dans le pacs (saisi manuellement ou déjà généré) :
  // conservé hors dépendances de l'effet d'init pour éviter que ce
  // dernier ne le régénère/écrase à chaque changement de registre/signataire.
  const pacsNumeroRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    pacsNumeroRef.current = pacs?.numero ?? null;
  }, [pacs?.numero]);

  // Le numéro n'est conservé que s'il a été saisi/modifié depuis l'écran de
  // saisie (handlePacsChange). Un numéro simplement auto-généré doit, lui,
  // pouvoir être recalculé quand le registre change (ex : changement de type de
  // déclaration depuis l'étape 1), sinon on garderait un numéro calculé à
  // partir de l'ancien registre.
  const numeroSaisiRef = React.useRef<boolean>(false);
  // Même information que numeroSaisiRef, mais exposée dans le contexte (un ref
  // ne déclenchant pas de rendu) : l'assistant en a besoin pour savoir s'il
  // doit avertir l'utilisateur que son numéro saisi a été corrigé.
  const [numeroSaisiManuellement, setNumeroSaisiManuellement] =
    React.useState<boolean>(false);

  const officierRef = React.useRef<{
    signataireNomFamille: string | null;
    signatairePrenom: string | null;
    signataireQualite: string | null;
  }>({
    signataireNomFamille: null,
    signatairePrenom: null,
    signataireQualite: null,
  });
  React.useEffect(() => {
    officierRef.current = {
      signataireNomFamille:
        pacs?.conventionDeclaration?.signataireNomFamille ?? null,
      signatairePrenom: pacs?.conventionDeclaration?.signatairePrenom ?? null,
      signataireQualite: pacs?.conventionDeclaration?.signataireQualite ?? null,
    };
  }, [
    pacs?.conventionDeclaration?.signataireNomFamille,
    pacs?.conventionDeclaration?.signatairePrenom,
    pacs?.conventionDeclaration?.signataireQualite,
  ]);

  const handlePacsChange = React.useCallback(
    (
      name: string,
      value: PacsChangeValue,
      isNumeroSaisiManuellement = true,
    ) => {
      if (name === 'numero') {
        numeroSaisiRef.current = isNumeroSaisiManuellement;
        setNumeroSaisiManuellement(isNumeroSaisiManuellement);
      }

      // Les champs de prénom sont mis en forme à la saisie (première lettre
      // en majuscule, reste en minuscule), comme sur etcv.
      const valeur = formatPrenomSaisie(name, value);

      setPacs((prev: any) => {
        const newPacs = _.cloneDeep(prev);

        _.set(newPacs, name, valeur);

        return newPacs;
      });
    },
    [],
  );

  // Initialiser pacs et appliquer les changements basés sur parametresUser
  // biome-ignore lint/correctness/useExhaustiveDependencies: resetVersion n'est pas lu ici, il sert à rejouer l'initialisation après un reset
  React.useEffect(() => {
    if (!_.isNil(selectedPacs) && selectedPacs !== -1) {
      setComputedPacsNumber(null);
      return;
    }

    const newPacs = _.cloneDeep(DEFAULT_PACS);
    const anneeRegistre = registre?.annee as any;
    const codePostal = anneeRegistre?.voie?.codePostal;
    const commune = codePostal?.commune;
    const departement = commune?.departement;

    // Initialisation des champs du PACS uniquement lors de création via type Projet et Initiale
    if (
      selectedDeclarationType !== 'modificative' &&
      selectedDeclarationType !== 'dissolution'
    ) {
      // si un numéro PACS est déjà présent on le garde.
      // sinon on en génère un nouveau à partir du registre
      const registreValue = registre as any;
      const nextPacsNumber = computeNextPacsNumber(registreValue);
      const existingNumero = numeroSaisiRef.current
        ? pacsNumeroRef.current
        : null;
      const numeroNewPacs = _.isNil(existingNumero)
        ? nextPacsNumber
        : existingNumero;

      _.set(newPacs, 'numero', numeroNewPacs);
      // computedPacsNumber sert de valeur de référence pour le reset
      // cas avec bouton dans InformationPACS et erreurs de création avec handleFinish
      setComputedPacsNumber(nextPacsNumber);

      // si section du registre est vide, alors on ne remplie pas les champs correspondants.
      const isCommuneDelegue =
        !_.isNil(registre?.section) && registre.section !== '';

      // Initialise le registre du PACS
      _.set(newPacs, 'registre.identifiant', registre?.identifiant);
      _.set(newPacs, 'registre.libelle', registre?.libelle);
      _.set(newPacs, 'registre.numero', registre?.numero);
      _.set(newPacs, 'registre.type', registre?.type);
      _.set(newPacs, 'registre.annee.identifiant', anneeRegistre?.identifiant);
      _.set(newPacs, 'registre.annee.annee', anneeRegistre?.annee);
      _.set(
        newPacs,
        'registre.numerotationManuelle',
        registre?.numerotationManuelle,
      );
      _.set(newPacs, 'registre.numeroOrdre', registre?.numeroOrdre);

      // Initialise les champs de l'officier d'état civil (si déjà saisis, on
      // les conserve plutôt que de les réécraser avec le signataire par défaut)
      if (signataire) {
        _.set(
          newPacs,
          'conventionDeclaration.signataireNomFamille',
          officierRef.current.signataireNomFamille ?? signataire?.nomFamille,
        );
        _.set(
          newPacs,
          'conventionDeclaration.signatairePrenom',
          officierRef.current.signatairePrenom ?? signataire?.prenom,
        );
        // Le grade correspond au champ « Grade signataire » de la fiche
        // officier (profession), et non au « Texte signataire » (signataire.titre)
        _.set(
          newPacs,
          'conventionDeclaration.signataireQualite',
          officierRef.current.signataireQualite ??
            signataire?.profession ??
            null,
        );
      }

      // Initialise dateEnregistrement par date du jour
      if (initDates === 1) {
        _.set(
          newPacs,
          'conventionDeclaration.dateEnregistrement',
          dayjs().format('YYYY-MM-DD'),
        );
      }

      // Initialise les adresses de naissance et nationalité des partenaires
      if (initLieuNaissance === 1) {
        // Partenaire 1
        _.set(
          newPacs,
          'conventionDeclaration.partenaire1.adresseNaissance.libelleCodePostal',
          codePostal?.codePostal,
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire1.adresseNaissance.libelleCommune',
          codePostal?.libelleDistribution,
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire1.adresseNaissance.libellePays',
          anneeRegistre?.pays ?? 'France',
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire1.adresseNaissance.libelleDepartement',
          departement?.libelle,
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire1.adresseNaissance.codeDepartement',
          departement?.code,
        );
        // Si pas de nationalité, Française par défaut
        _.set(
          newPacs,
          'conventionDeclaration.partenaire1.premiereNationalite',
          anneeRegistre?.pays?.nationaliteFeminin ?? 'Française',
        );
        // Si isCommuneDelegue, alors on set les communes déléguées
        if (isCommuneDelegue) {
          _.set(
            newPacs,
            'conventionDeclaration.partenaire1.adresseNaissance.naissanceLibelleCommuneDelegue',
            registre?.section,
          );
          _.set(
            newPacs,
            'conventionDeclaration.partenaire1.adresseNaissance.naissanceCommuneDelegue',
            1,
          );
        } else {
          _.set(
            newPacs,
            'conventionDeclaration.partenaire1.adresseNaissance.naissanceCommuneDelegue',
            0,
          );
        }

        // Partenaire 2
        _.set(
          newPacs,
          'conventionDeclaration.partenaire2.adresseNaissance.libelleCodePostal',
          codePostal?.codePostal,
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire2.adresseNaissance.libelleCommune',
          codePostal?.libelleDistribution,
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire2.adresseNaissance.libellePays',
          anneeRegistre?.pays ?? 'France',
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire2.adresseNaissance.libelleDepartement',
          departement?.libelle,
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire2.adresseNaissance.codeDepartement',
          departement?.code,
        );
        // Si pas de nationalité, Française par défaut
        _.set(
          newPacs,
          'conventionDeclaration.partenaire2.premiereNationalite',
          anneeRegistre?.pays?.nationaliteFeminin ?? 'Française',
        );
        // Si isCommuneDelegue, alors on set les communes déléguées
        if (isCommuneDelegue) {
          _.set(
            newPacs,
            'conventionDeclaration.partenaire2.adresseNaissance.naissanceLibelleCommuneDelegue',
            registre?.section,
          );
          _.set(
            newPacs,
            'conventionDeclaration.partenaire2.adresseNaissance.naissanceCommuneDelegue',
            1,
          );
        } else {
          _.set(
            newPacs,
            'conventionDeclaration.partenaire2.adresseNaissance.naissanceCommuneDelegue',
            0,
          );
        }

        // Mode Hubee : la demande ne porte pas le lieu de naissance des parents,
        // on ne le préremplit pas avec la commune du registre.
        if (_.isEmpty(hubeePrefill)) {
          // Mère partenaire 1
          _.set(
            newPacs,
            'conventionDeclaration.merePartenaire1.adresseNaissance.libelleCodePostal',
            codePostal?.codePostal,
          );
          _.set(
            newPacs,
            'conventionDeclaration.merePartenaire1.adresseNaissance.libelleCommune',
            codePostal?.libelleDistribution,
          );
          _.set(
            newPacs,
            'conventionDeclaration.merePartenaire1.adresseNaissance.libellePays',
            anneeRegistre?.pays ?? 'France',
          );
          _.set(
            newPacs,
            'conventionDeclaration.merePartenaire1.adresseNaissance.libelleDepartement',
            departement?.libelle,
          );
          _.set(
            newPacs,
            'conventionDeclaration.merePartenaire1.adresseNaissance.codeDepartement',
            departement?.code,
          );
          if (isCommuneDelegue) {
            _.set(
              newPacs,
              'conventionDeclaration.merePartenaire1.adresseNaissance.naissanceLibelleCommuneDelegue',
              registre?.section,
            );
            _.set(
              newPacs,
              'conventionDeclaration.merePartenaire1.adresseNaissance.naissanceCommuneDelegue',
              1,
            );
          } else {
            _.set(
              newPacs,
              'conventionDeclaration.merePartenaire1.adresseNaissance.naissanceCommuneDelegue',
              0,
            );
          }

          // Père partenaire 1
          _.set(
            newPacs,
            'conventionDeclaration.perePartenaire1.adresseNaissance.libelleCodePostal',
            codePostal?.codePostal,
          );
          _.set(
            newPacs,
            'conventionDeclaration.perePartenaire1.adresseNaissance.libelleCommune',
            codePostal?.libelleDistribution,
          );
          _.set(
            newPacs,
            'conventionDeclaration.perePartenaire1.adresseNaissance.libellePays',
            anneeRegistre?.pays ?? 'France',
          );
          _.set(
            newPacs,
            'conventionDeclaration.perePartenaire1.adresseNaissance.libelleDepartement',
            departement?.libelle,
          );
          _.set(
            newPacs,
            'conventionDeclaration.perePartenaire1.adresseNaissance.codeDepartement',
            departement?.code,
          );
          if (isCommuneDelegue) {
            _.set(
              newPacs,
              'conventionDeclaration.perePartenaire1.adresseNaissance.naissanceLibelleCommuneDelegue',
              registre?.section,
            );
            _.set(
              newPacs,
              'conventionDeclaration.perePartenaire1.adresseNaissance.naissanceCommuneDelegue',
              1,
            );
          } else {
            _.set(
              newPacs,
              'conventionDeclaration.perePartenaire1.adresseNaissance.naissanceCommuneDelegue',
              0,
            );
          }

          // Mère partenaire 2
          _.set(
            newPacs,
            'conventionDeclaration.merePartenaire2.adresseNaissance.libelleCodePostal',
            codePostal?.codePostal,
          );
          _.set(
            newPacs,
            'conventionDeclaration.merePartenaire2.adresseNaissance.libelleCommune',
            codePostal?.libelleDistribution,
          );
          _.set(
            newPacs,
            'conventionDeclaration.merePartenaire2.adresseNaissance.libellePays',
            anneeRegistre?.pays ?? 'France',
          );
          _.set(
            newPacs,
            'conventionDeclaration.merePartenaire2.adresseNaissance.libelleDepartement',
            departement?.libelle,
          );
          _.set(
            newPacs,
            'conventionDeclaration.merePartenaire2.adresseNaissance.codeDepartement',
            departement?.code,
          );
          if (isCommuneDelegue) {
            _.set(
              newPacs,
              'conventionDeclaration.merePartenaire2.adresseNaissance.naissanceLibelleCommuneDelegue',
              registre?.section,
            );
            _.set(
              newPacs,
              'conventionDeclaration.merePartenaire2.adresseNaissance.naissanceCommuneDelegue',
              1,
            );
          } else {
            _.set(
              newPacs,
              'conventionDeclaration.merePartenaire2.adresseNaissance.naissanceCommuneDelegue',
              0,
            );
          }

          // Père partenaire 2
          _.set(
            newPacs,
            'conventionDeclaration.perePartenaire2.adresseNaissance.libelleCodePostal',
            codePostal?.codePostal,
          );
          _.set(
            newPacs,
            'conventionDeclaration.perePartenaire2.adresseNaissance.libelleCommune',
            codePostal?.libelleDistribution,
          );
          _.set(
            newPacs,
            'conventionDeclaration.perePartenaire2.adresseNaissance.libellePays',
            anneeRegistre?.pays ?? 'France',
          );
          _.set(
            newPacs,
            'conventionDeclaration.perePartenaire2.adresseNaissance.libelleDepartement',
            departement?.libelle,
          );
          _.set(
            newPacs,
            'conventionDeclaration.perePartenaire2.adresseNaissance.codeDepartement',
            departement?.code,
          );
          if (isCommuneDelegue) {
            _.set(
              newPacs,
              'conventionDeclaration.perePartenaire2.adresseNaissance.naissanceLibelleCommuneDelegue',
              registre?.section,
            );
            _.set(
              newPacs,
              'conventionDeclaration.perePartenaire2.adresseNaissance.naissanceCommuneDelegue',
              1,
            );
          } else {
            _.set(
              newPacs,
              'conventionDeclaration.perePartenaire2.adresseNaissance.naissanceCommuneDelegue',
              0,
            );
          }
        }
      }

      if (initLieuDomicile === 1) {
        // Partenaire 1
        _.set(
          newPacs,
          'conventionDeclaration.partenaire1.adresseResidence.libelleCodePostal',
          codePostal?.codePostal,
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire1.adresseResidence.libelleCommune',
          codePostal?.libelleDistribution,
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire1.adresseResidence.libellePays',
          // France par défaut même si adresse vide que si paramètre pré remplit
          anneeRegistre?.pays ?? 'France',
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire1.adresseResidence.libelleDepartement',
          departement?.libelle,
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire1.adresseResidence.codeDepartement',
          departement?.code,
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire1.adresseResidence.article',
          commune?.article,
        );
        // Si isCommuneDelegue, alors on set les communes déléguées
        if (isCommuneDelegue) {
          _.set(
            newPacs,
            'conventionDeclaration.partenaire1.adresseResidence.residenceLibelleCommuneDelegue',
            registre?.section,
          );
          _.set(
            newPacs,
            'conventionDeclaration.partenaire1.adresseResidence.residenceCommuneDelegue',
            1,
          );
        } else {
          _.set(
            newPacs,
            'conventionDeclaration.partenaire1.adresseResidence.residenceCommuneDelegue',
            0,
          );
          _.set(
            newPacs,
            'conventionDeclaration.partenaire1.adresseResidence.residenceLibelleCommuneDelegue',
            null,
          );
        }

        // Partenaire 2
        _.set(
          newPacs,
          'conventionDeclaration.partenaire2.adresseResidence.libelleCodePostal',
          codePostal?.codePostal,
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire2.adresseResidence.libelleCommune',
          codePostal?.libelleDistribution,
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire2.adresseResidence.libellePays',
          anneeRegistre?.pays ?? 'France',
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire2.adresseResidence.libelleDepartement',
          departement?.libelle,
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire2.adresseResidence.codeDepartement',
          departement?.code,
        );
        _.set(
          newPacs,
          'conventionDeclaration.partenaire2.adresseResidence.article',
          commune?.article,
        );
        // Si isCommuneDelegue, alors on set les communes déléguées
        if (isCommuneDelegue) {
          _.set(
            newPacs,
            'conventionDeclaration.partenaire2.adresseResidence.residenceLibelleCommuneDelegue',
            registre?.section,
          );
          _.set(
            newPacs,
            'conventionDeclaration.partenaire2.adresseResidence.residenceCommuneDelegue',
            1,
          );
        } else {
          _.set(
            newPacs,
            'conventionDeclaration.partenaire2.adresseResidence.residenceCommuneDelegue',
            0,
          );
          _.set(
            newPacs,
            'conventionDeclaration.partenaire2.adresseResidence.residenceLibelleCommuneDelegue',
            null,
          );
        }
      }

      if (isCommuneDelegue) {
        _.set(
          newPacs,
          'conventionDeclaration.adresseDeclaration.libelleCommuneDelegue',
          registre?.section,
        );
        _.set(
          newPacs,
          'conventionDeclaration.adresseDeclaration.communeDelegue',
          1,
        );
        _.set(
          newPacs,
          'conventionDeclaration.adresseConvention.libelleCommuneDelegue',
          registre?.section,
        );
        _.set(
          newPacs,
          'conventionDeclaration.adresseConvention.communeDelegue',
          1,
        );
        _.set(
          newPacs,
          'conventionDeclaration.adressePacs.libelleCommuneDelegue',
          registre?.section,
        );
        _.set(newPacs, 'conventionDeclaration.adressePacs.communeDelegue', 1);
        _.set(
          newPacs,
          'conventionDeclaration.adresseCommune.libelleCommuneDelegue',
          registre?.section,
        );
        _.set(
          newPacs,
          'conventionDeclaration.adresseCommune.communeDelegue',
          1,
        );
      } else {
        _.set(
          newPacs,
          'conventionDeclaration.adresseDeclaration.communeDelegue',
          0,
        );
        _.set(
          newPacs,
          'conventionDeclaration.adresseConvention.communeDelegue',
          0,
        );
        _.set(newPacs, 'conventionDeclaration.adressePacs.communeDelegue', 0);
        _.set(
          newPacs,
          'conventionDeclaration.adresseCommune.communeDelegue',
          0,
        );
        _.set(
          newPacs,
          'conventionDeclaration.adresseCommune.libelleCommuneDelegue',
          null,
        );
      }

      // Rattachement au référentiel transverse. Le registre renvoie les objets
      // code postal / commune / département imbriqués, donc leurs identifiants
      // sont disponibles ici
      const setAdresseReferentiel = (
        adresseKey: string,
        withCodeDepartement = false,
      ) => {
        const basePath = `conventionDeclaration.${adresseKey}`;

        _.set(
          newPacs,
          `${basePath}.identifiantCodePostal`,
          codePostal?.identifiant ?? null,
        );
        _.set(
          newPacs,
          `${basePath}.identifiantCommune`,
          commune?.identifiant ?? null,
        );
        _.set(
          newPacs,
          `${basePath}.identifiantDepartement`,
          commune?.identifiantDepartement ?? departement?.identifiant ?? null,
        );

        if (withCodeDepartement) {
          _.set(
            newPacs,
            `${basePath}.codeDepartement`,
            departement?.code ?? null,
          );
        }
      };

      // Init d'Adresse Saisie PACS
      _.set(
        newPacs,
        'conventionDeclaration.adressePacs.localite',
        codePostal?.libelleDistribution,
      );
      _.set(
        newPacs,
        'conventionDeclaration.adressePacs.articleLocalite',
        commune?.article,
      );
      _.set(
        newPacs,
        'conventionDeclaration.adressePacs.codePostal',
        codePostal?.codePostal,
      );

      // Init d'Adresse Declaration Initiale
      _.set(
        newPacs,
        'conventionDeclaration.adresseDeclaration.libellePays',
        anneeRegistre?.pays,
      );
      _.set(
        newPacs,
        'conventionDeclaration.adresseDeclaration.libelleDepartement',
        departement?.libelle,
      );
      _.set(
        newPacs,
        'conventionDeclaration.adresseDeclaration.codePostal',
        codePostal?.codePostal,
      );
      _.set(
        newPacs,
        'conventionDeclaration.adresseDeclaration.libelleCommune',
        codePostal?.libelleDistribution,
      );
      _.set(
        newPacs,
        'conventionDeclaration.adresseDeclaration.localite',
        codePostal?.libelleDistribution,
      );
      _.set(
        newPacs,
        'conventionDeclaration.adresseDeclaration.lettre',
        commune?.article,
      );
      setAdresseReferentiel('adresseDeclaration', true);

      // Init d'Adresse Convention Initiale
      _.set(
        newPacs,
        'conventionDeclaration.adresseConvention.libellePays',
        anneeRegistre?.pays,
      );
      _.set(
        newPacs,
        'conventionDeclaration.adresseConvention.libelleDepartement',
        departement?.libelle,
      );
      _.set(
        newPacs,
        'conventionDeclaration.adresseConvention.codePostal',
        codePostal?.codePostal,
      );
      _.set(
        newPacs,
        'conventionDeclaration.adresseConvention.libelleCommune',
        codePostal?.libelleDistribution,
      );
      _.set(
        newPacs,
        'conventionDeclaration.adresseConvention.localite',
        codePostal?.libelleDistribution,
      );
      _.set(
        newPacs,
        'conventionDeclaration.adresseConvention.lettre',
        commune?.article,
      );
      setAdresseReferentiel('adresseConvention', true);
    } else {
      setComputedPacsNumber(null);
    }

    // Mode Hubee : les données de la demande dématérialisée sont fusionnées
    // par-dessus l'initialisation. Le mapping ne renvoie que les champs
    // réellement portés par le télédossier, les valeurs issues du registre et
    // du paramétrage (numéro, officier) sont donc conservées ; une adresse de
    // la demande remplace en revanche celle du registre en entier.
    setPacs(
      _.isEmpty(hubeePrefill)
        ? newPacs
        : fusionnerPrefillHubee(newPacs, hubeePrefill),
    );
  }, [
    initDates,
    initLieuNaissance,
    initLieuDomicile,
    registre,
    signataire,
    selectedPacs,
    selectedDeclarationType,
    resetVersion,
    hubeePrefill,
  ]);

  const handleChange = React.useCallback(
    (
      _e: React.SyntheticEvent | null,
      {
        name,
        value,
        checked,
      }: { name: string; value?: PacsChangeValue; checked?: boolean },
    ) => {
      handlePacsChange(name, checked ?? value ?? null);
    },
    [handlePacsChange],
  );

  const handleLoad = React.useCallback((data: any) => {
    setPacs(data);
  }, []);

  // numéro non modifiable en modificative/dissolution ni lors de la reprise
  // d'un PACS existant, sauf cas ou conversion projet en initiale
  const isNumeroLocked = React.useMemo(() => {
    const isReprisePacs =
      selectedPacs !== null &&
      selectedPacs !== undefined &&
      selectedPacs !== -1;

    return (
      selectedDeclarationType === 'modificative' ||
      selectedDeclarationType === 'dissolution' ||
      (isReprisePacs && selectedDeclarationType !== 'initiale')
    );
  }, [selectedPacs, selectedDeclarationType]);

  // Réinitialise le numéro de PACS à la valeur calculée automatiquement
  // (utilisé par le bouton de reset de InformationsPACS ainsi qu'en cas
  // d'erreur de création liée à un numéro déjà attribué).
  // On passe par handlePacsChange (et non handleChange/onChange) en
  // indiquant explicitement que ce n'est pas une saisie manuelle : sinon
  // numeroSaisiRef resterait bloqué à `true` et computedPacsNumber ne
  // serait plus jamais recalculé à partir du registre (cf bug où le reset
  // finissait par se figer sur le dernier numéro saisi/en erreur).
  const resetPacsNumero = React.useCallback(() => {
    if (
      isNumeroLocked ||
      computedPacsNumber === null ||
      computedPacsNumber === undefined ||
      computedPacsNumber === ''
    ) {
      return;
    }

    handlePacsChange('numero', computedPacsNumber, false);
  }, [isNumeroLocked, computedPacsNumber, handlePacsChange]);

  // Repositionne le numéro de PACS sur une valeur imposée par le back
  // (dernier numéro du registre + 1, cf. handleFinish de l'assistant).
  // Ce n'est pas une saisie utilisateur : on le marque comme non saisi pour
  // que le numéro reste recalculable si le registre change, et on l'utilise
  // comme nouvelle valeur de référence du bouton de reset.
  const setPacsNumero = React.useCallback(
    (numero: string) => {
      handlePacsChange('numero', numero, false);
      setComputedPacsNumber(numero);
    },
    [handlePacsChange],
  );

  // Reset toute les informations saisies dans l'assistant, sauf le type de déclaration
  // sélectionné
  const handleReset = React.useCallback((options?: ResetOptions) => {
    pacsNumeroRef.current = null;
    numeroSaisiRef.current = false;
    setNumeroSaisiManuellement(false);
    officierRef.current = {
      signataireNomFamille: null,
      signatairePrenom: null,
      signataireQualite: null,
    };

    setPacs(_.cloneDeep(DEFAULT_PACS));
    setErrors({});
    setComputedPacsNumber(null);
    setTeledossierData(null);
    setSelectedPacs(null);

    if (options?.keepDeclarationType !== true) {
      setSelectedDeclarationType(null);
      setIsDissolutionMode(false);
    }

    setResetVersion((version) => version + 1);
  }, []);

  const context = React.useMemo<AssistantContextType>(
    () => ({
      ...pacs,
      computedPacsNumber,
      isNumeroLocked,
      isNumeroSaisiManuellement: numeroSaisiManuellement,
      resetPacsNumero,
      setPacsNumero,
      errors,
      setErrors,
      onChange: handleChange,
      onReset: handleReset,
      onLoad: handleLoad,
      teledossierData,
      setTeledossierData,
      // Traitement d'une demande dématérialisée en cours
      isHubeeMode: !_.isNil(teledossierData),
      isLoadingHubeeData,
      setIsLoadingHubeeData,
      isDissolutionMode,
      setIsDissolutionMode,
      selectedDeclarationType,
      setSelectedDeclarationType,
      selectedPacs,
      setSelectedPacs,
    }),
    [
      pacs,
      computedPacsNumber,
      isNumeroLocked,
      numeroSaisiManuellement,
      resetPacsNumero,
      setPacsNumero,
      errors,
      handleChange,
      handleReset,
      handleLoad,
      teledossierData,
      isLoadingHubeeData,
      isDissolutionMode,
      selectedDeclarationType,
      selectedPacs,
    ],
  );

  return (
    <AssistantContext.Provider value={context}>
      {children}
    </AssistantContext.Provider>
  );
};

export default AssistantContextProvider;
